import { PathtypesButtons, locationsArr, paycyclesArr, customerTypesArr } from '../../../../../constants/data_arrays';
import DateField from '../../../../../controls/Date';
import TextField from '../../../../../controls/TextField';
import OldAutoComplete from '../../../../../controls/Autocomplete';
import { Autocomplete, Divider, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { FC, useMemo, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import moment, { isMoment, Moment, MomentInput } from 'moment';
import { underwritingOptions } from '../../../../../constants/underwritingOptions';
import { scrollToFirstError } from '../../../../../utils/scrollToError';
import ModelsUnderwritingTable from '../../../../models/components/organisms/ModelsTable';
import { DropResult } from 'react-beautiful-dnd';
import { MUIDataTableColumn } from 'mui-datatables';
import { Autorenew, CallMissed, ListAlt, ThumbsUpDown } from '@mui/icons-material';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { PathFilter } from '../../../api';
import Button from '../../../../../components/atoms/Button/Button';
import { Path } from '../../../api/generated';
import { FilterIds } from '../../../../../constants/filterIds';

export type DateLike = Date | Moment | string;
export function toMoment(d?: MomentInput, pattern?: string): Moment {
    if (isMoment(d)) {
        return d;
    }
    return moment(d, pattern);
}

export function toDate(d: DateLike): Date {
    if (d instanceof Date) {
        return d;
    }
    return toMoment(d).toDate();
}

export function zodDate() {
    return z.preprocess((date) => {
        if (isMoment(date)) {
            return date.toDate();
        } else if (date instanceof Date) {
            return date;
        } else if (typeof date === 'string') {
            return moment(date).toDate();
        }
    }, z.date());
}

const schema = z
    .object({
        name: z.string().min(1, 'Path name is required'),
        activeStart: zodDate(),
        activeEnd: zodDate(),
        pathType: z.string().min(1, "Path type is required"),
        origins: z.string().array().optional(),
        states: z.string().array().optional(),
        paycycles: z.string().array().optional(),
        retentionScores: z.string().array().optional(),
        customerTypes: z.string().array().optional(),
        pathModels: z
            .object({
                modelId: z.number()
            })
            .array()
            .superRefine((val, ctx) => {
                var valueArr = val.map(function (item) { return item.modelId });
                var isDuplicate = valueArr.some(function (item, idx) {
                    return valueArr.indexOf(item) !== idx
                });
                if (isDuplicate === true)
                    ctx.addIssue(
                        {
                            code: z.ZodIssueCode.custom,
                            message: 'No duplicates allowed',
                        })
                if (valueArr.length === 0)
                    ctx.addIssue(
                        {
                            code: z.ZodIssueCode.custom,
                            message: 'Please select at least one model.',
                        })
            }),

    })
    .superRefine(({ activeStart, activeEnd }, ctx) => {
        if (activeStart && activeEnd && !moment(activeStart).isBefore(moment(activeEnd))) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Start Date must be earlier than End Date',
                path: ['activeStart'],
            });
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'End Date must be later than Start Date',
                path: ['activeEnd'],
            });
        }
    })
    .refine(
        ({ origins, states, paycycles, retentionScores, customerTypes }) => {
            if ((origins?.length ?? 0) > 0 || (states?.length ?? 0) > 0 || (paycycles?.length ?? 0) > 0 || (retentionScores?.length ?? 0) > 0 || (customerTypes?.length ?? 0) > 0) {
                return true;
            }
        },
        {
            message: 'Please select at least one filter.',
            // NOTE: All filters share one validation logic. RHF Does not like custom paths for errors, so I had to pick one of the form inputs to hold it
            path: ['origins'],
        },
    );

type PathsFormProps = {
    onSubmit: (p: Path) => void;
    onDelete?: (p: number) => void;
    path?: Path;
    models: {
        modelId: number;
        name: string;
        modelType: 'Decision' | 'Deny' | 'Price' | 'Risk' | 'BidWinRate' | 'Conversion';
    }[];
    submitLoading?: boolean;
    delLoading?: boolean;
    copymodels?: any[];
    filters?: any[];
};

const modelCols: MUIDataTableColumn[] = [
    {
        name: 'modelId',
        options: {
            display: 'false',
            filter: false,
        },
    },

    {
        name: 'name',
        label: 'Model Name',
    },
    {
        name: 'modelType',
        label: 'Type',
        options: {
            customBodyRender: (cell: string | undefined | null) => {
                let icon;

                switch (cell) {
                    case 'Decision':
                        icon = <ThumbsUpDown />;
                        break;
                    case 'Deny':
                        icon = <ListAlt />;
                        break;
                    case 'Price':
                        icon = <Autorenew />;
                        break;
                    case 'Risk':
                        icon = <CallMissed />;
                        break;
                    default:
                        icon = '';
                }
                const tooltipTitle = cell ?? 'unknown';
                return (
                    <Tooltip title={tooltipTitle}>
                        <IconButton size="large">{icon} </IconButton>
                    </Tooltip>
                );
            }, //Above switch decides what icon to display based on the model type
        },
    },
];

//shouldnt need closeModal
const PathsForm: FC<PathsFormProps> = ({
    path,
    models,
    onSubmit,
    onDelete,
    delLoading,
    submitLoading,
    copymodels,
    filters,
}) => {
    const {
        setValue,
        watch,
        handleSubmit,
        control,
        reset,
        trigger,
        formState: { errors },
    } = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        shouldFocusError: false,
    });

    const formRef = useRef<HTMLFormElement>(null);

    // This probably shouldn't be here. Will improvise later on..

    const selectedModels = useMemo(() => {
        const sourceModels = (copymodels ?? []).length > 0 ? copymodels ?? [] : path?.pathModels ?? [];

        return sourceModels.reduce<any[]>((pv, cv) => {
            const foundModel = models.find((x) => x.modelId === cv.modelId);

            if (foundModel?.modelId && foundModel?.name) {
                return [
                    ...pv,
                    {
                        modelId: foundModel.modelId,
                        name: foundModel.name,
                        modelType: foundModel.modelType,
                        sortOrder: cv?.sortOrder,
                    },
                ];
            }
            return pv;
        }, []);
    }, [path, models, copymodels]);

    const sourceFilters = (filters ?? []).length > 0 ? filters ?? [] : path?.pathFilterModels ?? [];

    const defaultOrigins = sourceFilters
        .filter((x: PathFilter) => x.filterId === FilterIds.Origin)
        .map((x: PathFilter) => x.filterValues)
        .filter((v): v is string => typeof v === 'string' && v.length > 0);

    const defaultStates = sourceFilters
        .filter((x: PathFilter) => x.filterId === FilterIds.State)
        .map((x: PathFilter) => x.filterValues)
        .filter((v): v is string => typeof v === 'string' && v.length > 0);

    const defaultPaycycles = sourceFilters
        .filter((x: PathFilter) => x.filterId === FilterIds.PayCycle)
        .map((x: PathFilter) => x.filterValues)
        .filter((v): v is string => typeof v === 'string' && v.length > 0);

    const defaultRetentionScores = sourceFilters
        .filter((x: PathFilter) => x.filterId === FilterIds.RetentionScore)
        .map((x: PathFilter) => x.filterValues)
        .filter((v): v is string => typeof v === 'string' && v.length > 0);

    const defaultCustomerTypes = sourceFilters
        .filter((x: PathFilter) => x.filterId === FilterIds.CustomerType)
        .map((x: PathFilter) => x.filterValues)
        .filter((v): v is string => typeof v === 'string' && v.length > 0);

    useEffect(() => {
        reset({
            name: path?.name ?? '',
            pathType: path?.pathType ?? '',
            activeStart: path?.activeStart ? moment(path.activeStart) : moment(),
            activeEnd: path?.activeEnd ? moment(path.activeEnd) : moment('12/31/2099'),
            origins: defaultOrigins ?? [],
            states: defaultStates ?? [],
            paycycles: defaultPaycycles ?? [],
            retentionScores: defaultRetentionScores ?? [],
            customerTypes: defaultCustomerTypes ?? [],
            pathModels: selectedModels ?? [],
        });
    }, [path?.pathId, reset, copymodels, filters]);

    const pathModels = watch('pathModels', selectedModels);

    let defaultPathType = path?.pathType;

    /**
    * Function for drag and drop functionality in the models table included
    * @param result
    * @returns
    */
    const handleDragEnd = (result: DropResult) => {
        const { destination, source } = result;

        if (!destination) {
            return;
        }

        const values = [...(pathModels || [])];

        const element = values.splice(source.index, 1)[0];
        values.splice(destination.index, 0, element);

        setValue('pathModels', values);
    };

    // we want this in here because its only used by this component
    const toFilters = (states?: string[], origins?: string[], paycycles?: string[], retentionScores?: string[], customerTypes?: string[]) => {
        var temp: PathFilter[] = [];

        states?.forEach((value) => {
            temp.push({ filterId: FilterIds.State, filterValues: value });
        });

        origins?.forEach((value) => {
            temp.push({ filterId: FilterIds.Origin, filterValues: value });
        });

        paycycles?.forEach((value) => {
            temp.push({ filterId: FilterIds.PayCycle, filterValues: value });
        });

        retentionScores?.forEach((value) => {
            temp.push({ filterId: FilterIds.RetentionScore, filterValues: value })
        });

        customerTypes?.forEach((value) => {
            temp.push({ filterId: FilterIds.CustomerType, filterValues: value, });
        });

        return temp;
    };
    const toPath = (p: z.infer<typeof schema>): Path => ({
        pathId: 0,
        name: p.name,
        pathType: p.pathType,
        activeStart: moment(p.activeStart).format(),
        activeEnd: moment(p.activeEnd).format(),
        pathFilterModels: toFilters(p?.states, p?.origins, p?.paycycles, p?.retentionScores, p?.customerTypes),
        pathModels: p?.pathModels.map((v, i) => {
            return {
                modelId: v.modelId,
                sortOrder: i + 1,
            };
        }),
    });

    return (
        <form
            ref={formRef}
            onSubmit={handleSubmit(
                (v) => {
                    // convert to  Path and send off
                    return onSubmit(toPath(v));
                },
                (errors) => scrollToFirstError(formRef.current, errors)
            )}
        >
            <Grid container alignContent={'flex-start'} justifyContent={'center'} direction={'row'} spacing={2}>
                <Grid item xs={4} sm={5}>
                    <Controller
                        control={control}
                        name={'name'}
                        rules={{ required: 'Required' }}
                        defaultValue={path?.name ?? ''}
                        render={({ ref, ...rest }) => (
                            <TextField
                                {...rest}
                                fullWidth
                                label={'Name'}
                                error={Boolean(errors.name)}
                                helperText={errors.name?.message || ' '}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={4} sm={5}>
                    <Controller
                        control={control}
                        name={'pathType'}
                        rules={{ required: 'Required' }}
                        defaultValue={defaultPathType ?? ''}
                        render={({ onChange, ...rest }) => (
                            <Autocomplete
                                fullWidth
                                data-testid="PathTypeDD"
                                {...rest}
                                options={PathtypesButtons}
                                onChange={(_, data) => {
                                    return onChange(data);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        error={Boolean(errors.pathType)}
                                        helperText={(errors.pathType as any)?.message || ' '}
                                        label="Path type"
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={4} sm={5}>
                    <Controller
                        control={control}
                        name={'activeStart'}
                        defaultValue={moment(path?.activeStart) ?? moment()}
                        render={({ ref, onChange, ...rest }) => (
                            <DateField
                                {...rest}
                                onChange={(m: Moment | null) => {
                                    onChange(m);
                                    trigger(['activeStart', 'activeEnd']);
                                }}
                                label={'Active Start'}
                                fullWidth
                                error={Boolean(errors.activeStart)}
                                helperText={errors.activeStart?.message || ' '}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={4} sm={5}>
                    <Controller
                        control={control}
                        name={'activeEnd'}
                        defaultValue={path?.activeEnd ? moment(path?.activeEnd) : moment('12/31/2099')}
                        render={({ ref, onChange, ...rest }) => (
                            <DateField
                                {...rest}
                                onChange={(m: Moment | null) => {
                                    onChange(m);
                                    trigger(['activeStart', 'activeEnd']);
                                }}
                                label={'Active End'}
                                fullWidth
                                error={Boolean(errors.activeEnd)}
                                helperText={errors.activeEnd?.message || ' '}
                            />
                        )}
                    />
                </Grid>

                {/*----- FILTERS -----*/}
                {/* NOTE ON VALIDATION AND ERRORS: I am using origins as the holder for validation errors and messages
           /* as all 3 filters share a validation rule */}
                <Grid item xs={12}>
                    <Typography> Path Filters: </Typography>
                    <Divider variant="fullWidth" />
                </Grid>
                <Grid item xs={5} sm={10}>
                    <Controller
                        control={control}
                        name="origins"
                        defaultValue={defaultOrigins ?? []}
                        render={({ onChange, ...rest }) => (
                            <OldAutoComplete
                                options={[]}
                                label={'Origins'}
                                {...rest}
                                onChange={(e, v) => {
                                    const pasteMerge = v.reduce<string[]>((c, p) => {
                                        const n = p.split(';');
                                        for (let i = 0; i < n.length; i++) {
                                            c.push(n[i]);
                                        }
                                        return c;
                                    }, []);
                                    setValue('origins', pasteMerge);
                                }}
                                freeSolo={true}
                                fullWidth
                                error={Boolean(errors.origins)}
                                helperText={(errors.origins as any)?.message || ' '}
                                limitTags={10}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={5} sm={10}>
                    <Controller
                        control={control}
                        name="states"
                        defaultValue={defaultStates ?? []}
                        render={({ onChange, ...rest }) => (
                            <Autocomplete
                                options={locationsArr}
                                {...rest}
                                fullWidth
                                multiple={true}
                                onChange={(_, data) => {
                                    return onChange(data);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        error={Boolean(errors.origins)}
                                        helperText={(errors.origins as any)?.message || ' '}
                                        label="States"
                                    />
                                )}
                                limitTags={5}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={5} sm={10}>
                    <Controller
                        control={control}
                        name="paycycles"
                        defaultValue={defaultPaycycles ?? []}
                        render={({ onChange, ...rest }) => (
                            <Autocomplete
                                options={paycyclesArr}
                                {...rest}
                                fullWidth
                                multiple={true}
                                onChange={(_, data) => onChange(data)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        error={Boolean(errors.origins)}
                                        helperText={(errors.origins as any)?.message || ' '}
                                        label="Paycycles"
                                    />
                                )}
                                limitTags={5}
                            />
                        )}
                    ></Controller>
                </Grid>
                <Grid item xs={5} sm={10}>
                    <Controller
                        control={control}
                        name="retentionScores"
                        defaultValue={defaultRetentionScores ?? []}
                        render={({ onChange, ...rest }) => (
                            <OldAutoComplete
                                options={[]}
                                label={'Retention Scores'}
                                {...rest}
                                onChange={(e, v) => {
                                    const pasteMerge = v.reduce<string[]>((c, p) => {
                                        const n = p.split(';');
                                        for (const i of n) {
                                            c.push(i);
                                        }
                                        return c;
                                    }, []);
                                    setValue('retentionScores', pasteMerge);
                                }}
                                freeSolo={true}
                                fullWidth
                                error={Boolean(errors.origins)}
                                helperText={(errors.origins as any)?.message || ' '}
                                limitTags={10}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={5} sm={10}>
                    <Controller
                        control={control}
                        name="customerTypes"
                        defaultValue={defaultCustomerTypes ?? []}
                        render={({ onChange, ...rest }) => (
                            <Autocomplete
                                options={customerTypesArr}
                                {...rest}
                                fullWidth
                                multiple
                                onChange={(_, data) => onChange(data)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        error={Boolean(errors.origins)}
                                        helperText={(errors.origins as any)?.message || ' '}
                                        label="Customer Types"
                                    />
                                )}
                                limitTags={5}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Divider variant="fullWidth" />
                </Grid>
                <Grid item xs={10} sm={10}>
                    <Controller
                        control={control}
                        name="pathModels"
                        defaultValue={selectedModels ?? []}
                        render={({ onChange, ...rest }) => (
                            <Autocomplete
                                {...rest}
                                fullWidth
                                multiple={true}
                                onChange={(_, data) => {
                                    onChange(data);
                                }}
                                options={models ?? []}
                                getOptionLabel={(x) => {
                                    return x.name;
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        error={Boolean(errors.pathModels)}
                                        helperText={(errors.pathModels as any)?.message || ' '}
                                        label="Models"
                                    />
                                )}
                                limitTags={20}
                            />
                        )}
                    ></Controller>
                </Grid>
                {/*--- MODELS TABLE FOR SORTING ---*/}
                <Grid item xs={10} sm={10}>
                    <ModelsUnderwritingTable
                        droppableId="pathModels"
                        columns={modelCols}
                        data={pathModels ?? []}
                        title="Drag to Reorder Selected Models"
                        options={underwritingOptions}
                        onDragEnd={handleDragEnd}
                        onRowClick={() => { }}
                    />
                </Grid>
                <Grid item xs={10}>
                    <Stack spacing={2} direction="row">
                        {/*  this should honestly be moved up back to PathsViewModal so we don't have to pass these props but not worrying about it for now. */}
                        {onDelete !== undefined && (
                            <Button
                                color="error"
                                fullWidth
                                loading={delLoading}
                                onClick={() => {
                                    if (path && path.pathId) {
                                        return onDelete(path.pathId);
                                    }
                                }}
                            >
                                Delete Path
                            </Button>
                        )}
                        {/*  this should honestly be moved up back to PathsViewModal so we don't have to pass these props but not worrying about it for now. */}
                        <Button fullWidth loading={submitLoading} type="submit">
                            Save
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
        </form>
    );
};
export default PathsForm;
